import { type FC, type FormEvent, useEffect, useState } from "react";
import {
  AppBar,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { MarkdownInput } from "@/shared/components/MarkdownInput";
import {
  AnnotationPositionType,
  TimecodeIconType,
  TimecodeTypeType,
} from "@/types/graphql/partner.generated";
import { TimeInput } from "./TimeInput";
import { IconSelect } from "./IconSelect";
import { LinkedContentField } from "./LinkedContentField";
import type { EditableTimecode } from "./types";

interface TimecodeFormDialogProps {
  open: boolean;
  onClose: () => void;
  onCompleted: (timecode: EditableTimecode) => void;
  mode: "create" | "edit" | null;
  /** Текущее время плеера — стартовое значение для новой метки. */
  currentTime?: number;
  /** Метка для режима edit. */
  timecode?: EditableTimecode | null;
  videoDuration?: number;
}

// Порт референсного VideoForm/Timecodes/TimecodeFormDialog.js: форма
// создания/редактирования метки. Для MARKER — plain-описание, для
// ANNOTATION — markdown (только ссылки), duration/position/linkedContent.
export const TimecodeFormDialog: FC<TimecodeFormDialogProps> = ({
  open,
  onClose,
  onCompleted,
  mode,
  currentTime = 0,
  timecode = null,
  videoDuration = 0,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [type, setType] = useState<TimecodeTypeType>(
    TimecodeTypeType.MarkerType
  );
  const [time, setTime] = useState(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState<TimecodeIconType>(
    TimecodeIconType.PlayArrowType
  );
  const [duration, setDuration] = useState(5);
  const [position, setPosition] = useState<AnnotationPositionType>(
    AnnotationPositionType.TopRightType
  );
  const [linkedContentId, setLinkedContentId] = useState<string | null>(null);

  const isEditMode = mode === "edit";
  const isAnnotation = type === TimecodeTypeType.AnnotationType;

  // Инициализация значений при открытии диалога (референсный useEffect)
  useEffect(() => {
    if (!open) return;
    if (isEditMode && timecode) {
      setType(timecode.type || TimecodeTypeType.MarkerType);
      setTime(timecode.time);
      setTitle(timecode.title);
      setDescription(timecode.description ?? "");
      setIcon(timecode.icon);
      setDuration(timecode.duration ?? 5);
      setPosition(timecode.position ?? AnnotationPositionType.TopRightType);
      setLinkedContentId(
        timecode.linkedContentId ?? timecode.linkedContent?.id ?? null
      );
    } else if (!isEditMode) {
      setType(TimecodeTypeType.MarkerType);
      setTime(Math.round(currentTime));
      setTitle("");
      setDescription("");
      setIcon(TimecodeIconType.PlayArrowType);
      setDuration(5);
      setPosition(AnnotationPositionType.TopRightType);
      setLinkedContentId(null);
    }
  }, [open, isEditMode, timecode, currentTime]);

  const resetFields = () => {
    setType(TimecodeTypeType.MarkerType);
    setTime(0);
    setTitle("");
    setDescription("");
    setIcon(TimecodeIconType.PlayArrowType);
    setDuration(5);
    setPosition(AnnotationPositionType.TopRightType);
    setLinkedContentId(null);
  };

  const handleSave = () => {
    if (!title.trim()) {
      enqueueSnackbar(t("videos.timecodes.titleRequired"), {
        variant: "error",
      });
      return;
    }

    if (videoDuration > 0 && time > videoDuration) {
      enqueueSnackbar(t("videos.timecodes.timeExceedsVideoError"), {
        variant: "error",
      });
      return;
    }

    if (isAnnotation) {
      if (!duration || duration < 1 || duration > 30) {
        enqueueSnackbar(t("videos.timecodes.durationError"), {
          variant: "error",
        });
        return;
      }
      if (!position) {
        enqueueSnackbar(t("videos.timecodes.positionError"), {
          variant: "error",
        });
        return;
      }
    }

    const timecodeData: EditableTimecode = {
      id: timecode?.id,
      type,
      time,
      title: title.trim(),
      description: description.trim(),
      icon,
      duration: isAnnotation ? Math.trunc(duration) : null,
      position: isAnnotation ? position : null,
      linkedContentId: isAnnotation ? (linkedContentId ?? null) : null,
    };

    resetFields();
    onCompleted(timecodeData);
  };

  const handleClose = () => {
    resetFields();
    onClose();
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleSave();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={onSubmit}>
        <AppBar color="default" position="sticky" elevation={0}>
          <Toolbar>
            <IconButton edge="start" onClick={handleClose} sx={{ mr: 1 }}>
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
              {isEditMode
                ? t("videos.timecodes.edit")
                : t("videos.timecodes.create")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={
                !title.trim() || (videoDuration > 0 && time > videoDuration)
              }
            >
              {t("common.actions.save")}
            </Button>
          </Toolbar>
          <Divider />
        </AppBar>

        <DialogContent sx={{ p: 2 }}>
          <Card elevation={0}>
            <CardContent sx={{ pt: 3 }}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  {isEditMode ? (
                    <TextField
                      label={t("videos.timecodes.type")}
                      value={
                        isAnnotation
                          ? t("videos.timecodes.annotation")
                          : t("videos.timecodes.marker")
                      }
                      fullWidth
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                    />
                  ) : (
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>{t("videos.timecodes.type")}</InputLabel>
                      <Select
                        value={type}
                        onChange={(event) =>
                          setType(event.target.value as TimecodeTypeType)
                        }
                        label={t("videos.timecodes.type")}
                      >
                        <MenuItem value="MARKER">
                          {t("videos.timecodes.marker")}
                        </MenuItem>
                        <MenuItem value="ANNOTATION">
                          {t("videos.timecodes.annotation")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TimeInput
                    label={t("videos.timecodes.time")}
                    value={time}
                    onChange={setTime}
                    videoDuration={videoDuration}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <IconSelect
                    value={icon}
                    onChange={(value) => setIcon(value as TimecodeIconType)}
                    label={t("videos.timecodes.icon")}
                    fullWidth
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    autoFocus
                    label={t("videos.timecodes.title")}
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    fullWidth
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid size={12}>
                  {isAnnotation ? (
                    <MarkdownInput
                      value={description}
                      onChange={setDescription}
                      linksOnly
                    />
                  ) : (
                    <TextField
                      label={t("videos.timecodes.description")}
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      fullWidth
                      variant="outlined"
                      multiline
                      rows={3}
                    />
                  )}
                </Grid>
                {isAnnotation && (
                  <>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label={t("videos.timecodes.duration")}
                        type="number"
                        value={duration}
                        onChange={(event) =>
                          setDuration(Number(event.target.value))
                        }
                        fullWidth
                        variant="outlined"
                        inputProps={{ min: 1, max: 30 }}
                        helperText={t("videos.timecodes.durationHint")}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>
                          {t("videos.timecodes.position")}
                        </InputLabel>
                        <Select
                          value={position}
                          onChange={(event) =>
                            setPosition(
                              event.target.value as AnnotationPositionType
                            )
                          }
                          label={t("videos.timecodes.position")}
                        >
                          <MenuItem value="TOP_LEFT">
                            {t("videos.timecodes.topLeft")}
                          </MenuItem>
                          <MenuItem value="TOP_RIGHT">
                            {t("videos.timecodes.topRight")}
                          </MenuItem>
                          <MenuItem value="BOTTOM_LEFT">
                            {t("videos.timecodes.bottomLeft")}
                          </MenuItem>
                          <MenuItem value="BOTTOM_RIGHT">
                            {t("videos.timecodes.bottomRight")}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={12}>
                      <LinkedContentField
                        value={linkedContentId}
                        valueTitle={
                          timecode?.linkedContentId === linkedContentId ||
                          timecode?.linkedContent?.id === linkedContentId
                            ? timecode?.linkedContent?.title
                            : null
                        }
                        onChange={setLinkedContentId}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </DialogContent>
      </form>
    </Dialog>
  );
};
